import React, { useCallback, useEffect, useRef, useState } from "react";
import { TVChartContainer } from "./TVChartContainer";
import useTVChart, { TransSub } from "./initConfig/useTVChart";
import { Empty } from "antd";
import "./App.css";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { GloablMessageSub, GlobalMessageType, PostMessageType } from "./rxjs";
import {
  EntityId,
  IChartingLibraryWidget,
  IPositionLineAdapter,
} from "tradingView";
import { useDebounceFn, useLocalStorageState, useMount } from "ahooks";
import Decimal from "decimal.js";
import { queryOnlineTrade } from "./http";
import {
  getLoginToken,
  numFormatter,
  postWindowMessage,
} from "./initConfig/utils";
import { TradeSwapHistory } from "./types";
import Socket from "./Socket";
import { colors } from "./initConfig/color";

dayjs.extend(utc);
dayjs.extend(timezone);

dayjs.tz.setDefault(dayjs.tz.guess());
export const DEV_ORIGIN = "https://localhost:3000";

function App() {
  const tvWidgetRef = useRef<IChartingLibraryWidget>(null);

  const {
    widgetProps,
    address,
    chain,
    walletAddress,
    symbol,
    loginToken,
    upColor,
    downColor,
    themeName,
    locale,
    hideBottom,
    hideLeft,
    hideTop,
    bgColor = colors.dark,
    ...props
  } = useTVChart({
    tvWidgetRef,
  });
  const walletAddressRef = useRef(walletAddress || "");

  useEffect(() => {
    const handle = (e: MessageEvent<any>) => {
      const { type, data } = e.data;
      switch (type) {
        case GlobalMessageType.TRADE_CHANGE:
          GloablMessageSub.next({ type: GlobalMessageType.TRADE_CHANGE });
          break;
        case GlobalMessageType.WALLET_ADDRESS_CHANGE:
          GloablMessageSub.next({
            type: GlobalMessageType.WALLET_ADDRESS_CHANGE,
            data,
          });
          break;
        case GlobalMessageType.KLINE_PARAMS:
          GloablMessageSub.next({
            type: GlobalMessageType.KLINE_PARAMS,
            data,
          });
          break;
        case GlobalMessageType.KLINE_DATA:
          // const filtered = data.filter(
          //   (i: any) => i.txID && i.token === address
          // );
          // if (!filtered.length) return;
          // filtered.forEach((i: any) => {
          //   // 服务器默认+8时区
          //   i.unix = dayjs.tz(i.timedate).unix();
          // });
          // // console.log("KLINE_DATA", data[0].priceUsdt);
          // TransSub.next(data);
          break;
        case GlobalMessageType.KLINE_PRICE_FIRST:
          data.unix = dayjs.tz(data.timedate).unix();
          TransSub.next([data]);
          break;
      }
    };
    window.addEventListener("message", handle);

    postWindowMessage(PostMessageType.KLINE_READY);

    return () => window.removeEventListener("message", handle);
  }, []);

  const tradeHistoryEntitys = useRef<Set<EntityId>>(new Set());
  const avgLine = useRef<IPositionLineAdapter | undefined>();
  const tradeDataRef = useRef<TradeSwapHistory[]>([]);
  const [showMarketStorage = true] = useLocalStorageState<boolean>(
    "showMarket",
    { listenStorageChange: true }
  );
  const [supply, setSupply] = useState(1000000000);

  const getMarkKline = useCallback(
    async (walletAddress: string) => {
      if (!tradeDataRef.current.length) {
        const tradeHistory = await queryOnlineTrade({
          token: address,
          owner: walletAddress,
        });
        const tradeJson = await tradeHistory.json();
        let tradeData: TradeSwapHistory[] = tradeJson.result;
        tradeData.reverse();
        tradeDataRef.current = tradeData;
      }
      return tradeDataRef.current;
    },
    [address]
  );

  // 交易记录相关标记
  const markKline = useCallback(
    async (walletAddress: string) => {
      try {
        const token = getLoginToken();
        if (!token || !address || !walletAddress) return;
        const tradeData = await getMarkKline(walletAddress);
        const clearIndex = tradeData.reduce(
          (pre, cur, curIndex) => {
            let value = pre.value.add(
              cur.direction === "buy"
                ? Number(cur.tokenAmount)
                : 0 - Number(cur.tokenAmount)
            );
            if (value.toNumber() <= 0) {
              return { value, index: curIndex };
            }
            return { value, index: pre.index };
          },
          {
            index: 0,
            value: new Decimal(0),
          }
        );
        const avgBuyDatas = tradeData
          .slice(clearIndex.index)
          .filter((i) => i.direction === "buy");

        const avgBuyPrice =
          avgBuyDatas.length === 1
            ? Number(avgBuyDatas[0].priceUsdt)
            : avgBuyDatas
                .reduce(
                  (pre, cur) =>
                    pre.add(new Decimal(cur.tokenAmount).times(cur.priceUsdt)),
                  new Decimal(0)
                )
                .div(
                  avgBuyDatas.reduce(
                    (pre, cur) => pre.add(cur.tokenAmount),
                    new Decimal(0)
                  )
                )
                .toNumber();

        const restAmount = clearIndex.value.toNumber();

        if (avgLine.current) {
          try {
            avgLine.current.remove();
          } catch {
            avgLine.current = undefined;
          }
        }
        if (tradeHistoryEntitys.current.size) {
          tradeHistoryEntitys.current.forEach((i) => {
            try {
              tvWidgetRef.current?.activeChart().removeEntity(i);
            } catch {}
          });
          tradeHistoryEntitys.current.clear();
        }
        if (!!tvWidgetRef.current) {
          if (restAmount > 0) {
            avgLine.current = tvWidgetRef.current
              .activeChart()
              .createPositionLine();
            avgLine.current
              .setLineColor("#ffd039")
              .setPrice(avgBuyPrice)
              .setText("Avg Price")
              .setBodyBackgroundColor("#ffffff00")
              .setBodyTextColor("#ffd039")
              .setBodyBorderColor("#ffd039")
              .setQuantity("")
              // OverrideLineStyle.Dashed
              .setLineStyle(2);
            // avg线并没有使用默认的formatter需要重新写入一个
            (avgLine.current as any)._line._formatter.format = (
              price: number
            ) => {
              return numFormatter(showMarketStorage ? price * supply : price);
            };
          }

          const tradeMarks: any[] = tradeData.map((item) => ({
            id: item.txId,
            time: item.createTime,
            color: item.direction === "buy" ? "green" : "red",
            text: `Total: $${numFormatter(Number(item.priceUsdt) * Number(item.tokenAmount))}(${numFormatter(Number(item.baseAmount))} SOL)\nAmount: ${numFormatter(Number(item.tokenAmount))}\nPrice: $${numFormatter(Number(item.priceUsdt))}`,
            label: item.direction === "buy" ? "B" : "S",
            labelFontColor: "white",
            minSize: 20,
            price: Number(item.priceUsdt),
          }));
          tradeMarks.forEach((item) => {
            const up = item.label === "B";
            const entityId = tvWidgetRef.current?.activeChart().createShape(
              { time: item.time, price: Number(item.price) },
              {
                shape: "emoji",
                // icon: up ? 0xf055 : 0xf056,
                lock: true,
                disableSelection: true,
                text: item.text,
                zOrder: "top",
                overrides: {
                  emoji: up ? "🇧" : "🇸",
                  size: 15,
                },
              }
            );
            if (entityId) {
              tradeHistoryEntitys.current.add(entityId);
            }
          });
        }
      } catch (err) {
        console.log(err);
      }
    },
    [address, showMarketStorage, supply]
  );

  const { run: markKlineDebounceFn } = useDebounceFn(markKline, { wait: 1000 });

  useEffect(() => {
    const sub = GloablMessageSub.subscribe(({ type, data }) => {
      switch (type) {
        case GlobalMessageType.GET_MAKRS:
          markKlineDebounceFn(walletAddressRef.current);
          break;
        // tradingview绘制dev的时间
        // 上级window传递事件，更新交易记录
        case GlobalMessageType.TRADE_CHANGE:
          tradeDataRef.current = [];
          markKlineDebounceFn(walletAddressRef.current);
          break;
        // 钱包切换或初始化
        case GlobalMessageType.WALLET_ADDRESS_CHANGE:
          walletAddressRef.current = data;
          tradeDataRef.current = [];
          markKlineDebounceFn(walletAddressRef.current);
          break;
        case GlobalMessageType.WATCH_WALLET_TRADE_CHANGE:
          tradeDataRef.current.push({
            createTime: dayjs.tz(data.timedate).valueOf(),
            direction: data.type,
            owner: data.owner,
            token: data.token,
            txId: data.txID,
            priceUsdt: data.priceUsdt,
            baseAmount: data.baseAmount,
            tokenAmount: data.tokenAmount,
          });
          markKlineDebounceFn(walletAddressRef.current);
          break;
        case GlobalMessageType.GET_TOKEN_INFO:
          setSupply(Number(data.result.supply));
          break;
      }
    });
    return () => sub.unsubscribe();
  }, []);

  useEffect(() => {
    tradeDataRef.current = [];
  }, [address]);

  useEffect(() => {
    if (avgLine.current) {
      (avgLine.current as any)._line._formatter.format = (price: number) => {
        return numFormatter(showMarketStorage ? price * supply : price);
      };
    }
  }, [showMarketStorage, supply]);

  return (
    <div style={{ height: "100vh" }}>
      {!!address ? (
        <TVChartContainer
          address={address}
          widgetProps={widgetProps}
          tvWidgetRef={tvWidgetRef}
          themeName={themeName}
          locale={locale}
          hideBottom={hideBottom}
          hideLeft={hideLeft}
          hideTop={hideTop}
          symbol={symbol}
          chain={chain}
          upColor={upColor}
          downColor={downColor}
          bgColor={bgColor}
        />
      ) : (
        <Empty description="No address" />
      )}
      <Socket
        chain={chain}
        address={address}
        walletAddressRef={walletAddressRef}
      />
    </div>
  );
}

export default App;
